import { HelpCircle } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button } from "@/shared/components/ui/button"
import { cn } from "@/shared/lib/utils"
import {
  CONFIG_SECTIONS,
  type ConfigSectionKey,
} from "./config-help-registry"
import { useConfigHelpEnabled, useOpenConfig } from "./use-config-help"

interface EditConfigLinkProps {
  /** Which config section this surface/card maps to. */
  section: ConfigSectionKey
  /** Label for the surface the user is on now (shown in the return pill). */
  returnLabel: string
  className?: string
}

/**
 * Inline "Edit config" link placed on a config-driven card or surface. Jumps to
 * the dedicated config page that drives that surface and records where to
 * return to. Renders nothing for users without config-help access.
 */
export function EditConfigLink({
  section,
  returnLabel,
  className,
}: EditConfigLinkProps) {
  const { t } = useTranslation()
  const enabled = useConfigHelpEnabled()
  const openConfig = useOpenConfig()
  const entry = CONFIG_SECTIONS[section]

  if (!enabled || !entry) return null

  return (
    <Button
      variant="link"
      size="xs"
      className={cn("h-auto px-0 text-[var(--ink-2)]", className)}
      onClick={() => openConfig(entry.primaryRoute, returnLabel)}
      title={entry.primaryLabel}
    >
      <HelpCircle aria-hidden />
      {t("configHelp.editConfig", { defaultValue: "Edit config" })}
    </Button>
  )
}
